import { useState, useEffect, useMemo } from "react"
import type { ReactNode } from "react"
import type { PageCursor, UserProfile } from "@ezbatna/core/domain/models"
import { getContainer } from "../lib/container"
import { Button, ConfirmDialog, DataTable, Input, StatusPill, useToast, type DataTableColumn } from "../components/ui"

const PAGE_SIZE = 20
const APP_URL = "https://app.ezbatna.com"

type PendingAction =
  | { kind: "make-moderator"; user: UserProfile }
  | { kind: "remove-role"; user: UserProfile }
  | { kind: "disable"; user: UserProfile }
  | { kind: "purge"; user: UserProfile }

const actionCopy: Record<PendingAction["kind"], { title: string; description: (u: UserProfile) => ReactNode; confirmLabel: string; destructive: boolean }> = {
  "make-moderator": {
    title: "منح صلاحية المشرف؟",
    description: (u) => <>سيحصل «{u.name ?? u.id}» على صلاحيات المشرف ويصبح بإمكانه إدارة المستخدمين والمحتوى.</>,
    confirmLabel: "منح الصلاحية",
    destructive: false,
  },
  "remove-role": {
    title: "إزالة صلاحيات هذا المستخدم؟",
    description: (u) => <>سيفقد «{u.name ?? u.id}» صلاحيات الإشراف الممنوحة له فوراً.</>,
    confirmLabel: "إزالة الصلاحية",
    destructive: true,
  },
  disable: {
    title: "تعطيل هذا الحساب؟",
    description: (u) => (
      <>
        لن يتمكن «{u.name ?? u.id}» من تسجيل الدخول أو استخدام التطبيق بعد التعطيل. يمكن التراجع عن هذا الإجراء لاحقاً من نفس الصفحة.
      </>
    ),
    confirmLabel: "تعطيل الحساب",
    destructive: true,
  },
  purge: {
    title: "حذف الحساب نهائياً؟",
    description: (u) => (
      <>
        سيتم جدولة حذف حساب «{u.name ?? u.id}» فوراً بشكل <strong>نهائي ولا يمكن التراجع عنه</strong> — تُحذف جميع بياناته وخدماته
        وتعليقاته وتقييماته بشكل دائم. تأكد قبل المتابعة.
      </>
    ),
    confirmLabel: "حذف نهائي",
    destructive: true,
  },
}

export function UsersPage() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [cursor, setCursor] = useState<PageCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [search, setSearch] = useState("")
  const toast = useToast()

  const fetchUsers = async (after?: PageCursor | null) => {
    setLoading(true)
    try {
      const page = await getContainer().userProfileRepository.fetchPage(PAGE_SIZE, after)
      setUsers((prev) => (after ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const runPending = async () => {
    if (!pending) return
    setActionLoading(true)
    try {
      const { kind, user } = pending
      if (kind === "make-moderator") {
        await getContainer().adminRepository.setRole(user.id, "moderator")
        toast.show(`تم منح صلاحية المشرف لـ ${user.name ?? user.id}`, "success")
      } else if (kind === "remove-role") {
        await getContainer().adminRepository.removeRole(user.id)
        toast.show(`تم إزالة الصلاحيات من ${user.name ?? user.id}`, "success")
      } else if (kind === "disable") {
        await getContainer().adminRepository.setUserDisabled(user.id, true)
        toast.show(`تم تعطيل ${user.name ?? user.id}`, "success")
      } else if (kind === "purge") {
        await getContainer().adminRepository.forcePurge(user.id)
        toast.show(`تمت جدولة حذف حساب ${user.name ?? user.id} نهائياً`, "success")
      }
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setActionLoading(false)
      setPending(null)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^@/, "")
    if (!q) return users
    return users.filter((u) => u.name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q))
  }, [users, search])

  const openProfile = (u: UserProfile) => window.open(`${APP_URL}/user/${u.id}`, "_blank", "noopener,noreferrer")

  const columns: DataTableColumn<UserProfile>[] = [
    {
      key: "name",
      header: "الاسم",
      render: (u) => (
        <div>
          <p className="font-semibold">{u.name ?? "—"}</p>
          <p className="text-caption text-text-secondary">@{u.username}</p>
        </div>
      ),
    },
    { key: "email", header: "البريد", render: (u) => u.email ?? "—" },
    { key: "phone", header: "هاتف", render: (u) => u.phone ?? "—" },
    {
      key: "status",
      header: "الحالة",
      render: (u) =>
        u.deletionScheduledAt ? (
          <StatusPill variant="danger">مجدول للحذف</StatusPill>
        ) : (
          <StatusPill variant="success">نشط</StatusPill>
        ),
    },
    {
      key: "actions",
      header: "إجراءات",
      align: "center",
      render: (u) => (
        <div className="flex flex-wrap items-center justify-center gap-xs" onClick={(e) => e.stopPropagation()}>
          <Button variant="secondary" size="sm" onClick={() => setPending({ kind: "make-moderator", user: u })}>
            مشرف
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setPending({ kind: "remove-role", user: u })}>
            إزالة صلاحية
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setPending({ kind: "disable", user: u })}>
            تعطيل
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setPending({ kind: "purge", user: u })}>
            حذف نهائي
          </Button>
        </div>
      ),
    },
  ]

  const copy = pending ? actionCopy[pending.kind] : null

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">المستخدمون</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">
          إدارة المستخدمين والصلاحيات وتعطيل الحسابات — اضغط على أي صف لفتح الملف الشخصي في الموقع
        </p>
      </div>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="بحث بالاسم أو اسم المستخدم (@username)…"
      />

      {search && (
        <p className="text-caption text-text-secondary">{filtered.length} من {users.length} مستخدم محمّل</p>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(u) => u.id}
        loading={loading}
        onRowClick={openProfile}
        emptyTitle="لا يوجد مستخدمون"
      />

      {cursor && (
        <Button variant="secondary" onClick={() => fetchUsers(cursor)} loading={loading} className="self-start">
          عرض المزيد
        </Button>
      )}

      <ConfirmDialog
        open={!!pending}
        title={copy?.title ?? ""}
        description={pending && copy ? copy.description(pending.user) : undefined}
        confirmLabel={copy?.confirmLabel}
        destructive={copy?.destructive}
        loading={actionLoading}
        onConfirm={runPending}
        onCancel={() => setPending(null)}
      />
    </div>
  )
}
